import { getRoot, Instance, types } from "mobx-state-tree";
import { RootInstance } from "./main";
import { JSONSchema7 } from "json-schema";
import { isEqual } from "lodash";
import { DynamicReferenceScope } from "../helpers/modKeyReferenceRename";
import { normalizeDynamicFormData } from "../helpers/dynamicFormKeyRename";
import {
  addPerVideoFieldToDocument,
  createPerVideoDataDocument,
  getPerVideoDataFromSchema,
  PerVideoDataDocument,
} from "./perVideoDataDocument";

type PerVideoSchemaDataOptions = {
  bumpRenderKey?: boolean;
  runPlayer?: boolean;
};

export const DynamicDataModel = types
  .model({
    liveControlCurrentSchema: types.maybeNull(types.string),
    perVideoCurrentSchema: types.maybeNull(types.string),
    liveControlCurrentData: types.frozen(),
    liveControlInitialSchema: types.maybeNull(types.string),
    perVideoInitialSchema: types.maybeNull(types.string),
    initialLiveControlData: types.frozen(),
    perVideoSchemaRenderKey: types.optional(types.number, 0),
  })
  .views((self) => ({
    get perVideoCurrentData() {
      return getPerVideoDataFromSchema(self.perVideoCurrentSchema);
    },
    get hasUnsavedChanges() {
      return (
        self.liveControlInitialSchema !== self.liveControlCurrentSchema ||
        !isEqual(self.initialLiveControlData, self.liveControlCurrentData) ||
        self.perVideoInitialSchema !== self.perVideoCurrentSchema
      );
    },
    get hasUnsavedChangesPerVideo() {
      return self.perVideoInitialSchema !== self.perVideoCurrentSchema;
    },
    get hasUnsavedChangesLiveControl() {
      return (
        self.liveControlInitialSchema !== self.liveControlCurrentSchema ||
        !isEqual(self.initialLiveControlData, self.liveControlCurrentData)
      );
    },
  }))
  .actions((self) => ({
    markSchemasSaved(saved: {
      stateJsonSchemaStr?: string | null;
      settingsJsonSchemaStr?: string | null;
      settings?: string;
    }) {
      if (saved.stateJsonSchemaStr != null)
        self.perVideoInitialSchema = saved.stateJsonSchemaStr;
      if (saved.settingsJsonSchemaStr != null)
        self.liveControlInitialSchema = saved.settingsJsonSchemaStr;
      if (saved.settings !== undefined)
        self.initialLiveControlData = normalizeDynamicFormData(saved.settings);
    },
    setCurrentPerVideoDocument(
      document: PerVideoDataDocument,
      options: PerVideoSchemaDataOptions = {}
    ) {
      self.perVideoCurrentSchema = document.schemaString;
      if (options.bumpRenderKey) {
        self.perVideoSchemaRenderKey += 1;
      }

      const root = getRoot<RootInstance>(self);
      if (options.runPlayer !== false) {
        root.playerStore.runPlayer();
      }
      root.modsStore.checkAllModsForDataErrors();

      return document;
    },
    setInitialPerVideoSchema(schema: string, force = true) {
      if (force === false && self.hasUnsavedChangesPerVideo) return;
      const document = createPerVideoDataDocument(schema);
      self.perVideoInitialSchema = document.schemaString;
      this.setCurrentPerVideoDocument(document, { runPlayer: true });
    },
    setInitialLiveControSchemaAndData(
      schema: string,
      liveControlData: string | Record<string, unknown>,
      force = true
    ) {
      if (force === false && self.hasUnsavedChangesLiveControl) return;
      let treatedSchema = JSON.parse(schema);
      if (
        treatedSchema.properties === undefined ||
        Object.keys(treatedSchema.properties).length === 0
      )
        treatedSchema = {
          $schema: "http://json-schema.org/draft-07/schema",
          $id: "http://example.com/example.json",
          type: "object",
          title: "",
          description: "",
          default: {},
          examples: [],
          required: [],
          properties: {},
          additionalProperties: true,
        };
      treatedSchema = JSON.stringify(treatedSchema);
      self.liveControlInitialSchema = treatedSchema;
      self.liveControlCurrentSchema = treatedSchema;
      this.setInitialLiveControlData(liveControlData);
    },

    resetPerVideoSchemaAndData() {
      const document = createPerVideoDataDocument(null, {});
      self.perVideoInitialSchema = document.schemaString;
      this.setCurrentPerVideoDocument(document);
    },
    setInitialLiveControlData(data) {
      const normalizedData = normalizeDynamicFormData(data);
      self.initialLiveControlData = normalizedData;
      this.setCurrentLiveControlData(normalizedData);
    },

    setCurrentLiveControlSchema(schema: JSONSchema7) {
      self.liveControlCurrentSchema = JSON.stringify(schema);
      getRoot<RootInstance>(self).modsStore.checkAllModsForDataErrors();
    },

    setCurrentPerVideoDocumentFromForm(
      schema: JSONSchema7 | string | null | undefined,
      data: unknown,
      options: PerVideoSchemaDataOptions = {}
    ) {
      return this.setCurrentPerVideoDocument(
        createPerVideoDataDocument(schema, data),
        options
      );
    },
    addPerVideoDataField(
      key: string,
      fieldSchema: JSONSchema7,
      defaultValue: unknown
    ) {
      const document = addPerVideoFieldToDocument({
        currentSchema: self.perVideoCurrentSchema,
        key,
        fieldSchema,
        defaultValue,
      });
      this.setCurrentPerVideoDocument(document, {
        bumpRenderKey: true,
      });

      return document;
    },
    setCurrentLiveControlData(data) {
      self.liveControlCurrentData = normalizeDynamicFormData(data);
      getRoot<RootInstance>(self).playerStore.runPlayer();
      getRoot<RootInstance>(self).modsStore.checkAllModsForDataErrors();
    },

    updateModsWithRenamedKeys(
      oldKey: string,
      newKey: string,
      scope: DynamicReferenceScope = "data"
    ) {
      const root = getRoot<RootInstance>(self);
      const modsStore = root.modsStore;
      const platformStore = root.platformStore;

      platformStore.renameProjectDataReferences(oldKey, newKey, scope);

      if (modsStore && modsStore.updateDataKeyReferences) {
        modsStore.updateDataKeyReferences(oldKey, newKey, scope);
      }
    },
    bumpPerVideoSchemaRenderKey() {
      self.perVideoSchemaRenderKey += 1;
    },
  }));

export type IDynamicDataModel = Instance<typeof DynamicDataModel>;
